import type { ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Command, { type CommandListProps } from "@/v2-ui/command"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { useSelectorContext } from "./selector.context"

const SKELETON_COUNT = 5

export type SelectorListProps = CommandListProps & {
  emptyLabel?: string
  isLoading?: boolean
  error?: Error | null | undefined
  /** When true, forces the emptyLabel to display regardless of children. Use when search returned no results but non-search items are still rendered. */
  noResults?: boolean
  /** Content that stays visible regardless of loading state (e.g. currently selected item). */
  pinned?: ReactNode
  children: ReactNode
}

function SelectorList(props: SelectorListProps) {
  const {
    emptyLabel = "No results found",
    isLoading,
    error,
    noResults,
    pinned,
    className,
    children,
  } = props

  const { searchStatus, minSearchLengthRef } = useSelectorContext()

  function renderStatus() {
    if (error) {
      return <ErrorMessage error={error} />
    }

    if (searchStatus === "below-min") {
      return (
        <div className="selector-empty">
          Type at least {minSearchLengthRef.current} characters to see results
        </div>
      )
    }

    if (searchStatus === "ready" && noResults) {
      return <div className="selector-empty">{emptyLabel}</div>
    }

    return (
      <Command.Empty className="selector-empty">{emptyLabel}</Command.Empty>
    )
  }

  return (
    <Command.List className={cn("selector-list", className)}>
      {pinned && (!Array.isArray(pinned) || pinned.length > 0) && (
        <div className="selector-list-pinned">{pinned}</div>
      )}

      {isLoading ? (
        <div className="selector-skeleton" aria-busy="true">
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <div key={i} className="selector-skeleton-item">
              <div className="selector-skeleton-bar selector-skeleton-primary" />
              <div className="selector-skeleton-bar selector-skeleton-secondary" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {children}
          {renderStatus()}
        </>
      )}
    </Command.List>
  )
}

export default SelectorList
